import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import {
  cancelStoryImportJob as cancelStoryImportJobClient,
  continueStoryImportJob as continueStoryImportJobClient,
  deleteStoryImportDraft as deleteStoryImportDraftClient,
  getStoryImportClientConfig,
  getStoryImportJob as getStoryImportJobClient,
  getLatestStoryImportJob as getLatestStoryImportJobClient,
  getStoryImportQuote as getStoryImportQuoteClient,
  readStoryImportSummary as readStoryImportSummaryClient,
  retryStoryImportJob as retryStoryImportJobClient,
  startStoryImportProcessing as startStoryImportProcessingClient,
  StoryImportClientError,
  uploadStoryImportDraft as uploadStoryImportDraftClient,
} from "@/features/story-import/api/storyImportClient";
import type {
  StoryImportCreditQuote,
  StoryImportDraft,
  StoryImportErrorCode,
  StoryImportJob,
  StoryImportNotice,
  StoryImportStep,
  StoryImportSummary,
} from "@/features/story-import/story-import.types";
import {
  createStoryImportDraft,
  getStoryImportJobErrorTranslationKey,
  getStoryImportTranslationKey,
  mapStoryImportValidationError,
  shouldRestoreDismissedStoryImportJob,
} from "@/features/story-import/storyImportMappers";
import { validateStoryImportFile } from "@/features/story-import/story-import.types";

const dismissedJobsStorageKey = "story-import-dismissed-progress-jobs";

function loadDismissedJobIds() {
  if (typeof window === "undefined") {
    return [] as string[];
  }

  try {
    const raw = window.localStorage.getItem(dismissedJobsStorageKey);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((value): value is string => typeof value === "string")
      : [];
  } catch {
    return [];
  }
}

function persistDismissedJobIds(jobIds: string[]) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(dismissedJobsStorageKey, JSON.stringify(jobIds));
  } catch {
    // Ignore local persistence failures and keep the in-memory state moving.
  }
}

export interface StoryImportStoreDependencies {
  cancelStoryImportJob: typeof cancelStoryImportJobClient;
  continueStoryImportJob: typeof continueStoryImportJobClient;
  deleteStoryImportDraft: typeof deleteStoryImportDraftClient;
  getStoryImportJob: typeof getStoryImportJobClient;
  getLatestStoryImportJob: typeof getLatestStoryImportJobClient;
  getStoryImportQuote: typeof getStoryImportQuoteClient;
  readStoryImportSummary: typeof readStoryImportSummaryClient;
  retryStoryImportJob: typeof retryStoryImportJobClient;
  startStoryImportProcessing: typeof startStoryImportProcessingClient;
  uploadStoryImportDraft: typeof uploadStoryImportDraftClient;
}

export interface StoryImportStoreState {
  activeStep: StoryImportStep;
  currentJob: StoryImportJob | null;
  dismissedJobIds: string[];
  draft: StoryImportDraft | null;
  isRefreshing: boolean;
  isRetrying: boolean;
  isCanceling: boolean;
  isStarting: boolean;
  isUploading: boolean;
  notice: StoryImportNotice | null;
  quote: StoryImportCreditQuote | null;
  summary: StoryImportSummary | null;
  status: "idle" | "loading" | "ready" | "error";
  clearNotice: () => void;
  dismissProgressForJob: (jobId?: string | null) => void;
  continueProcessing: () => Promise<StoryImportJob | null>;
  hydrateJob: (jobId: string) => Promise<StoryImportJob | null>;
  hydrateLatestPendingJob: () => Promise<StoryImportJob | null>;
  hydrateSummary: (jobId?: string) => Promise<StoryImportSummary | null>;
  loadQuote: (
    draft?: Pick<
      StoryImportDraft,
      "fileName" | "fileSizeBytes" | "mimeType" | "projectId" | "storagePath"
    > | null,
  ) => Promise<StoryImportCreditQuote>;
  restoreProgressForJob: (jobId: string) => void;
  reset: () => void;
  retryProcessing: () => Promise<StoryImportJob>;
  cancelProcessing: () => Promise<StoryImportJob>;
  selectFile: (file: File, projectId?: string | null) => Promise<StoryImportDraft>;
  startProcessing: () => Promise<StoryImportJob>;
  cancelDraft: () => Promise<void>;
  setActiveStep: (step: StoryImportStep) => void;
}

const defaultDependencies: StoryImportStoreDependencies = {
  cancelStoryImportJob: cancelStoryImportJobClient,
  continueStoryImportJob: continueStoryImportJobClient,
  deleteStoryImportDraft: deleteStoryImportDraftClient,
  getStoryImportJob: getStoryImportJobClient,
  getLatestStoryImportJob: getLatestStoryImportJobClient,
  getStoryImportQuote: getStoryImportQuoteClient,
  readStoryImportSummary: readStoryImportSummaryClient,
  retryStoryImportJob: retryStoryImportJobClient,
  startStoryImportProcessing: startStoryImportProcessingClient,
  uploadStoryImportDraft: uploadStoryImportDraftClient,
};

function getErrorCode(error: unknown): StoryImportErrorCode {
  return error instanceof StoryImportClientError ? error.code : "generic";
}

function getStoryImportNotice(error: unknown): StoryImportNotice {
  return {
    kind: "error",
    message: getStoryImportTranslationKey(getErrorCode(error)),
  };
}

function upsertDismissedJobId(jobIds: string[], jobId: string) {
  if (jobIds.includes(jobId)) {
    return jobIds;
  }

  return [...jobIds, jobId];
}

function removeDismissedJobId(jobIds: string[], jobId: string) {
  return jobIds.filter((value) => value !== jobId);
}

function isStoryImportArchivedJob(job: StoryImportJob) {
  return job.status === "complete" || job.status === "completed";
}

function isSameTrackedJob(previousJob: StoryImportJob | null, nextJob: StoryImportJob) {
  return previousJob?.jobId === nextJob.jobId;
}

function wasTrackedImportJobActive(previousJob: StoryImportJob | null, nextJob: StoryImportJob) {
  if (!previousJob || !isSameTrackedJob(previousJob, nextJob)) {
    return false;
  }

  return (
    previousJob.status === "queued"
    || previousJob.status === "pending"
    || previousJob.status === "processing"
    || previousJob.status === "canceling"
  );
}

export function createStoryImportStore(
  deps: Partial<StoryImportStoreDependencies> = {},
) {
  const resolvedDeps: StoryImportStoreDependencies = {
    ...defaultDependencies,
    ...deps,
  };

  return createStore<StoryImportStoreState>((set, get) => ({
    activeStep: "upload",
    currentJob: null,
    dismissedJobIds: loadDismissedJobIds(),
    draft: null,
    isRefreshing: false,
    isRetrying: false,
    isCanceling: false,
    isStarting: false,
    isUploading: false,
    notice: null,
    quote: null,
    summary: null,
    status: "idle",

    loadQuote: async (draft) => {
      set({
        notice: null,
        status: "loading",
      });

      try {
        const quote = await resolvedDeps.getStoryImportQuote(draft);
        set({
          quote,
          status: "ready",
        });
        return quote;
      } catch (error) {
        set({
          notice: getStoryImportNotice(error),
          quote: null,
          status: "error",
        });
        throw error;
      }
    },

    selectFile: async (file, projectId = null) => {
      const validation = validateStoryImportFile(
        {
          fileName: file.name,
          fileSizeBytes: file.size,
          mimeType: file.type,
        },
        getStoryImportClientConfig().fileSizeLimitBytes,
      );

      if (!validation.ok) {
        const code = mapStoryImportValidationError(validation);
        const error = new StoryImportClientError(code, code);
        set({
          notice: getStoryImportNotice(error),
        });
        throw error;
      }

      set({
        isUploading: true,
        notice: null,
      });

      try {
        const draft = await resolvedDeps.uploadStoryImportDraft(
          createStoryImportDraft(file, projectId),
        );
        const quote = await resolvedDeps.getStoryImportQuote(draft);

        set({
          activeStep: "credit-estimate",
          draft,
          isUploading: false,
          quote,
          summary: null,
          status: "ready",
        });

        return draft;
      } catch (error) {
        set({
          isUploading: false,
          notice: getStoryImportNotice(error),
          status: "error",
        });
        throw error;
      }
    },

    startProcessing: async () => {
      const draft = get().draft;

      if (!draft) {
        const error = new StoryImportClientError("start-failed", "No import draft is loaded.");
        set({ notice: getStoryImportNotice(error) });
        throw error;
      }

      set({
        isStarting: true,
        notice: null,
      });

      try {
        const job = await resolvedDeps.startStoryImportProcessing(draft);
        const dismissedJobIds = removeDismissedJobId(get().dismissedJobIds, job.jobId);
        persistDismissedJobIds(dismissedJobIds);

        set({
          activeStep: "processing",
          currentJob: job,
          dismissedJobIds,
          isStarting: false,
          summary: null,
          status: "ready",
        });
        return job;
      } catch (error) {
        set({
          isStarting: false,
          notice: getStoryImportNotice(error),
          status: "error",
        });
        throw error;
      }
    },

    retryProcessing: async () => {
      const jobId = get().currentJob?.jobId;

      if (!jobId) {
        const error = new StoryImportClientError("retry-not-allowed", "No import job is available.");
        set({ notice: getStoryImportNotice(error) });
        throw error;
      }

      set({
        isRetrying: true,
        notice: null,
      });

      try {
        const job = await resolvedDeps.retryStoryImportJob(jobId);
        const dismissedJobIds = removeDismissedJobId(get().dismissedJobIds, jobId);
        persistDismissedJobIds(dismissedJobIds);

        set({
          activeStep: "processing",
          currentJob: job,
          dismissedJobIds,
          isRetrying: false,
          summary: null,
          status: "ready",
        });
        return job;
      } catch (error) {
        set({
          isRetrying: false,
          notice: getStoryImportNotice(error),
          status: "error",
        });
        throw error;
      }
    },

    cancelProcessing: async () => {
      const jobId = get().currentJob?.jobId;

      if (!jobId) {
        const error = new StoryImportClientError("cancel-not-allowed", "No import job is available.");
        set({ notice: getStoryImportNotice(error) });
        throw error;
      }

      set({
        isCanceling: true,
        notice: null,
      });

      try {
        const job = await resolvedDeps.cancelStoryImportJob(jobId);
        set({
          activeStep: "processing",
          currentJob: job,
          isCanceling: false,
          status: "ready",
        });
        return job;
      } catch (error) {
        set({
          isCanceling: false,
          notice: getStoryImportNotice(error),
          status: "error",
        });
        throw error;
      }
    },

    hydrateSummary: async (jobId) => {
      const targetJobId = jobId ?? get().currentJob?.jobId;

      if (!targetJobId) {
        return null;
      }

      set({
        isRefreshing: true,
      });

      try {
        const summary = await resolvedDeps.readStoryImportSummary(targetJobId);
        set({
          isRefreshing: false,
          summary,
          status: "ready",
        });
        return summary;
      } catch (error) {
        set({
          isRefreshing: false,
          notice: getStoryImportNotice(error),
          status: "error",
          summary: null,
        });
        return null;
      }
    },

    cancelDraft: async () => {
      const storagePath = get().draft?.storagePath;

      if (storagePath) {
        await resolvedDeps.deleteStoryImportDraft(storagePath).catch(() => undefined);
      }

      set({
        activeStep: "upload",
        currentJob: null,
        draft: null,
        notice: null,
        summary: null,
      });
    },

    hydrateJob: async (jobId) => {
      set({ isRefreshing: true });

      try {
        const job = await resolvedDeps.getStoryImportJob(jobId);
        const previousJob = get().currentJob;

        if (job.errorText === "IMPORT_DISCARDED") {
          const dismissedJobIds = removeDismissedJobId(get().dismissedJobIds, job.jobId);
          persistDismissedJobIds(dismissedJobIds);
          set({
            activeStep: "upload",
            currentJob: null,
            draft: null,
            dismissedJobIds,
            isRefreshing: false,
            notice: {
              kind: "success",
              message: "storyImport.messages.discardSuccess",
            },
            quote: null,
            status: "ready",
            summary: null,
          });
          return null;
        }

        if (isStoryImportArchivedJob(job)) {
          const dismissedJobIds = removeDismissedJobId(get().dismissedJobIds, job.jobId);
          persistDismissedJobIds(dismissedJobIds);
          set({
            activeStep: "upload",
            currentJob: null,
            draft: null,
            dismissedJobIds,
            isRefreshing: false,
            notice: null,
            quote: null,
            status: "ready",
            summary: null,
          });
          return null;
        }

        if (job.status === "canceled") {
          const dismissedJobIds = removeDismissedJobId(get().dismissedJobIds, job.jobId);
          persistDismissedJobIds(dismissedJobIds);
          set({
            activeStep: "upload",
            currentJob: null,
            draft: null,
            dismissedJobIds,
            isRefreshing: false,
            notice: {
              kind: "success",
              message: "storyImport.messages.processingCanceled",
            },
            quote: null,
            status: "ready",
            summary: null,
          });
          return null;
        }

        const isReviewReadyJob =
          job.status === "review_ready";
        const didCompleteNow = isReviewReadyJob && wasTrackedImportJobActive(previousJob, job);
        const didFailNow =
          job.status === "failed"
          && isSameTrackedJob(previousJob, job)
          && previousJob?.status !== "failed";

        const nextState: Partial<StoryImportStoreState> = {
          activeStep:
            job.status === "queued"
            || job.status === "pending"
            || job.status === "processing"
            || job.status === "canceling"
            || job.status === "review_ready"
            || job.status === "importing"
            || job.status === "failed"
              ? "processing"
              : get().activeStep,
          currentJob: job,
          isRefreshing: false,
          status: "ready",
        };
        const shouldRestoreDismissed =
          get().dismissedJobIds.includes(job.jobId)
          && shouldRestoreDismissedStoryImportJob(previousJob, job);

        if (shouldRestoreDismissed) {
          const dismissedJobIds = removeDismissedJobId(get().dismissedJobIds, job.jobId);
          persistDismissedJobIds(dismissedJobIds);
          nextState.dismissedJobIds = dismissedJobIds;
        }

        if (isReviewReadyJob) {
          if (didCompleteNow) {
            nextState.notice = {
              kind: "success",
              message: "storyImport.messages.processingReady",
            };
          }

          if (job.summaryPath) {
            nextState.summary = await resolvedDeps.readStoryImportSummary(jobId).catch(() => null);
          }
        }

        if (job.status === "failed") {
          nextState.activeStep = "processing";
          nextState.notice = {
            kind: "error",
            message: getStoryImportJobErrorTranslationKey(job.errorText),
          };
          nextState.summary = null;
        }

        if (didFailNow && !nextState.notice) {
          nextState.notice = {
            kind: "error",
            message: getStoryImportJobErrorTranslationKey(job.errorText),
          };
        }

        set(nextState);
        return job;
      } catch (error) {
        set({
          isRefreshing: false,
          notice: getStoryImportNotice(error),
          status: "error",
        });
        return null;
      }
    },

    hydrateLatestPendingJob: async () => {
      set({ isRefreshing: true });

      try {
        const latestJob = await resolvedDeps.getLatestStoryImportJob({
          includeTerminal: true,
        });

        if (!latestJob) {
          set({
            isRefreshing: false,
            status: get().status === "idle" ? "idle" : "ready",
          });
          return null;
        }

        if (latestJob.status === "canceled" || latestJob.status === "failed") {
          set({
            isRefreshing: false,
            status: get().status === "idle" ? "idle" : "ready",
          });
          return null;
        }

        return await get().hydrateJob(latestJob.jobId);
      } catch (error) {
        set({
          isRefreshing: false,
          notice: getStoryImportNotice(error),
          status: "error",
        });
        return null;
      }
    },

    setActiveStep: (step) => {
      set({ activeStep: step });
    },

    clearNotice: () => {
      set({ notice: null });
    },

    dismissProgressForJob: (jobId) => {
      const targetJobId = jobId ?? get().currentJob?.jobId;
      if (!targetJobId) {
        return;
      }

      const dismissedJobIds = upsertDismissedJobId(get().dismissedJobIds, targetJobId);
      persistDismissedJobIds(dismissedJobIds);
      set({ dismissedJobIds });
    },

    continueProcessing: async () => {
      const currentJob = get().currentJob;
      if (
        !currentJob
          || (currentJob.status !== "queued"
          && currentJob.status !== "pending"
          && currentJob.status !== "processing"
          && currentJob.status !== "canceling")
      ) {
        return currentJob;
      }

      try {
        const job = await resolvedDeps.continueStoryImportJob(currentJob.jobId);
        set({
          currentJob: job,
        });
        return job;
      } catch {
        return currentJob;
      }
    },

    restoreProgressForJob: (jobId) => {
      const dismissedJobIds = removeDismissedJobId(get().dismissedJobIds, jobId);
      persistDismissedJobIds(dismissedJobIds);
      set({ dismissedJobIds });
    },

    reset: () => {
      set({
        activeStep: "upload",
        currentJob: null,
        draft: null,
        isRefreshing: false,
        isRetrying: false,
        isCanceling: false,
        isStarting: false,
        isUploading: false,
        notice: null,
        quote: null,
        summary: null,
        status: "idle",
      });
    },
  }));
}

export const storyImportStore = createStoryImportStore();

export function useStoryImportStore<T>(selector: (state: StoryImportStoreState) => T) {
  return useStore(storyImportStore, selector);
}
